"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShoppingBag, FolderTree, Sparkles, Layers, FileText, MousePointerClick, Image, Code2, GitBranch, Link2, Star, Truck, Smartphone, Tags, AlignLeft, SlidersHorizontal, FileStack, CopyX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
export default function ProductCategorySeo() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState<"product" | "category">("product");
  const productElements = [{
    title: "Product titles and H1s",
    description: "Product type, key attributes (size, material, colour) and brand where it reflects search behaviour.",
    icon: FileText
  }, {
    title: "Unique product descriptions",
    description: "Avoid copied supplier text; benefits, specifications and use-cases in scannable copy with bullet points and tables, written for humans first.",
    icon: AlignLeft
  }, {
    title: "Meta titles and descriptions",
    description: "Written for click-through: main keyword, value proposition, shipping or returns reassurance where relevant.",
    icon: MousePointerClick
  }, {
    title: "Images and alt text",
    description: "Show usage, scale and context; alt text describes the product and key attributes.",
    icon: Image
  }, {
    title: "Structured data and product schema",
    description: "Product schema (price, availability, ratings where genuine), validated with Google's Rich Results Test so data matches live information.",
    icon: Code2
  }, {
    title: "Variants (size, colour, pack size)",
    description: "Decisions about single vs multiple URLs, and canonical tags to prevent duplication.",
    icon: GitBranch
  }, {
    title: "Internal links",
    description: "Links to relevant categories, bundles and compatible accessories.",
    icon: Link2
  }, {
    title: "Customer reviews and Q&A",
    description: "Add trust and fresh content; long-tail queries often surface in genuine review text.",
    icon: Star
  }, {
    title: "Purchase information",
    description: "Clear stock status, UK and international delivery details, returns policy and payment options.",
    icon: Truck
  }, {
    title: "Mobile usability",
    description: "Fast load, easy add-to-basket, clear calls to action, thumb-friendly layout.",
    icon: Smartphone
  }];
  const categoryElements = [{
    title: "Category keyword targeting",
    description: "Core and supporting phrases (for example, 'garden chairs' primary; 'rattan garden chairs set of 4' a long-tail variant that might justify its own subcategory).",
    icon: Tags
  }, {
    title: "Architecture and hierarchy",
    description: "Logical nesting that reflects search demand (for example, Home > Garden > Outdoor Furniture > Garden Chairs).",
    icon: FolderTree
  }, {
    title: "Category introductions",
    description: "Short useful copy at the top; longer explanatory content below the product grid.",
    icon: AlignLeft
  }, {
    title: "Product grouping",
    description: "Depends on search demand; split by gender, use-case or price band only when enough people search that way.",
    icon: Layers
  }, {
    title: "Internal links and breadcrumbs",
    description: "Show relationships and distribute authority; breadcrumbs can appear in results.",
    icon: Link2
  }, {
    title: "Faceted filters",
    description: "Some combinations deserve indexable pages (such as 'blue velvet sofas'); others should be noindexed or canonicalised to prevent crawl waste.",
    icon: SlidersHorizontal
  }, {
    title: "Pagination",
    description: "rel/canonical patterns or 'view all' depending on catalogue size and crawl efficiency.",
    icon: FileStack
  }, {
    title: "Avoiding duplicate pages",
    description: "Clean rules for colour-based collections, overlapping sale/new-in pages, and tag-generated URLs.",
    icon: CopyX
  }];
  return <section id="product-category-seo" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-b border-border transition-colors duration-200">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-14">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Commercial Landing Pages
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            Product and category page SEO
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Where search visibility meets commercial revenue. I optimise both product pages and category collections to capture high-intent search queries and give shoppers the confidence to purchase.
          </p>
        </div>

        {/* Tab Switch Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
          <Button variant={activeTab === "product" ? "default" : "outline"} size="sm" onClick={() => setActiveTab("product")} aria-pressed={activeTab === "product"} className={`rounded-full px-5 py-2 text-xs sm:text-sm font-medium flex items-center gap-2 transition-all ${activeTab === "product" ? "shadow-xs" : "text-muted-foreground hover:text-foreground"}`}>
            <ShoppingBag className="size-3.5" />
            <span>Product page SEO</span>
          </Button>
          <Button variant={activeTab === "category" ? "default" : "outline"} size="sm" onClick={() => setActiveTab("category")} aria-pressed={activeTab === "category"} className={`rounded-full px-5 py-2 text-xs sm:text-sm font-medium flex items-center gap-2 transition-all ${activeTab === "category" ? "shadow-xs" : "text-muted-foreground hover:text-foreground"}`}>
            <FolderTree className="size-3.5" />
            <span>Category and collection page SEO</span>
          </Button>
        </div>

        {/* Tab Content Display */}
        {activeTab === "product" && <motion.div key="product-tab" initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} className="space-y-8">
            {/* Tab Intro */}
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                Search visibility meets revenue on your individual product pages; each element helps search engines understand the offer and gives shoppers the clarity they need to add items to their basket.
              </p>
            </div>

            {/* Product Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
              {productElements.map((item, i) => {
            const Icon = item.icon;
            return <Card key={item.title} data-index={i} className="border border-border/80 bg-card hover:border-primary/40 transition-all shadow-xs flex flex-col justify-between">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="size-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                          <Icon className="size-4" />
                        </div>
                        <span className="text-xs font-mono font-medium text-muted-foreground bg-muted/60 px-2 py-0.5 rounded">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <CardTitle className="text-base font-semibold text-foreground">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </CardContent>
                  </Card>;
          })}
            </div>
          </motion.div>}

        {activeTab === "category" && <motion.div key="category-tab" initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} className="space-y-8">
            {/* Tab Intro */}
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                Category and collection pages often drive more organic revenue than single product pages for non-branded commercial searches, capturing buyers when they are evaluating options.
              </p>
            </div>

            {/* Category Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pt-4">
              {categoryElements.map((item, i) => {
            const Icon = item.icon;
            return <Card key={item.title} data-index={i} className="border border-border/80 bg-card hover:border-primary/40 transition-all shadow-xs flex flex-col justify-between">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="size-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                          <Icon className="size-4" />
                        </div>
                        <span className="text-xs font-mono font-medium text-muted-foreground bg-muted/60 px-2 py-0.5 rounded">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <CardTitle className="text-base font-semibold text-foreground">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </CardContent>
                  </Card>;
          })}
            </div>
          </motion.div>}
      </div>
    </section>;
}